import styled from '@emotion/styled'
import parse from 'html-react-parser'
import moment from 'moment'
import { useMemo } from 'react'
import Breakpoint from '../../Breakpoint'
import CardLink from './CardLink'

const PriceCard = ({
    title,
    price,
    midSeasonPrice,
    midSeasonDate,
    reductions,
    description,
    displayStartDate,
    displayEndDate,
}: {
    title: string
    price: number
    midSeasonPrice?: number
    midSeasonDate?: string
    description: string
    reductions?: { name: string; priceReduction: number }[]
    displayStartDate?: string
    displayEndDate?: string
}) => {
    const isMidSeason = useMemo(() => {
        const now = moment()

        // If midSeasonDate is defined, use it to determine mid-season
        if (midSeasonDate && midSeasonPrice) {
            const startMidSeason = moment(midSeasonDate)
            // Use displayEndDate as end of mid-season if defined, otherwise end of year
            const endMidSeason = displayEndDate ? moment(displayEndDate) : moment(`${now.year()}-12-31`)

            return now.isSameOrAfter(startMidSeason) && now.isSameOrBefore(endMidSeason)
        }

        // Fallback to hardcoded dates if midSeasonDate is not defined
        const currentYear = now.year()
        const startMidSeason = moment(`${currentYear}-02-01`) // 1er février
        const endMidSeason = moment(`${currentYear}-09-01`) // 1er septembre

        return now.isSameOrAfter(startMidSeason) && now.isBefore(endMidSeason)
    }, [midSeasonDate, displayEndDate, midSeasonPrice])

    const isWithinDisplayPeriod = useMemo(() => {
        const now = moment()

        if (displayStartDate && displayEndDate) {
            const start = moment(displayStartDate)
            const end = moment(displayEndDate)
            return now.isSameOrAfter(start) && now.isSameOrBefore(end)
        }

        if (displayStartDate) {
            const start = moment(displayStartDate)
            return now.isSameOrAfter(start)
        }

        if (displayEndDate) {
            const end = moment(displayEndDate)
            return now.isSameOrBefore(end)
        }

        return true
    }, [displayStartDate, displayEndDate])

    if (!isWithinDisplayPeriod) {
        return null
    }

    return (
        <PriceCardContainer>
            <PriceCardSubTitle>
                {title} {isMidSeason && midSeasonPrice && 'mi-saison'}
            </PriceCardSubTitle>
            <PriceCardTitle>{isMidSeason && midSeasonPrice ? midSeasonPrice : price}€</PriceCardTitle>
            <PriceCardDescription>{parse(description)}</PriceCardDescription>
            <Reductions>
                {!isMidSeason &&
                    reductions?.map((reduction) => (
                        <Reduction
                            key={`reduction-${reduction.name}`}
                        >{`${reduction.name}: ${reduction.priceReduction}€`}</Reduction>
                    ))}
            </Reductions>
            <PriceCardLinkContainer>
                <CardLink href="https://adherer.ffbad.club/BVA59" target="_blank">
                    {"S'inscrire"}
                </CardLink>
            </PriceCardLinkContainer>
        </PriceCardContainer>
    )
}

const PriceCardTitle = styled.h2`
    text-align: center;
    font-size: 4.8rem;
    margin-top: 16px;
    color: var(--primary-color);
    font-family: 'Roboto Semi Bold';
`

const PriceCardSubTitle = styled.h2`
    text-align: center;
    font-size: 3rem;
    margin-top: 8px;
    margin-bottom: 16px;
    color: var(--primary-color);
    font-family: 'Roboto Semi Bold';
`

const Reductions = styled.div`
    margin-bottom: 16px;
`

const Reduction = styled.div`
    color: var(--tertiary-color);
`

const PriceCardDescription = styled.div`
    text-align: justify;
    line-height: 22px;
    min-height: 80px;
    overflow: auto;
    padding-right: 13px;
    margin-bottom: 5px;
`

const PriceCardContainer = styled.article`
    position: relative;
    width: 98%;
    height: auto;
    display: flex;
    flex-direction: column;
    background: white;
    padding: 16px;
    border-radius: 6px;
    margin-right: 5px;
    box-sizing: border-box;
    margin-bottom: 16px;
    overflow: hidden;

    @media (max-width: ${Breakpoint.LAPTOP_XS}) {
        margin-bottom: 16px;
    }
`

const PriceCardLinkContainer = styled.div`
    display: flex;
    text-decoration: none;
    border-radius: 4px;
    flex: 1;
    justify-content: flex-end;
    align-items: flex-end;
`

export default PriceCard
